export type ProductFeature = {
  title: string;
  description: string;
};

export type Product = {
  slug: string;
  moonbaseId: string;
  name: string;
  category: string;
  eyebrow: string;
  shortDescription: string;
  description: string;
  priceFallback: string;
  listPriceFallback: string;
  artwork: string;
  accent: string;
  version: string;
  formats: readonly string[];
  systemRequirements: readonly string[];
  features: readonly ProductFeature[];
  idealFor: readonly string[];
  commerce: {
    trialDays: number;
    activations: number;
    launchDuration: string;
    educationPrice: string;
    educationListPrice: string;
  };
};

export const siteConfig = {
  brand: {
    name: 'Wavecraft Audio',
    shortName: 'Wavecraft',
    monogram: 'WA',
    tagline: 'Sound, shaped with intent.',
    description:
      'Focused, musical audio tools built to make complex changes feel immediate, expressive and easy to trust.',
  },
  company: {
    legalName: 'Wavecraft Audio',
    supportEmail: 'wavecraft-audio@wavecraft-audio.com',
    pressEmail: 'wavecraft-audio@wavecraft-audio.com',
    securityEmail: 'wavecraft-audio@wavecraft-audio.com',
    address: '',
    country: 'United States',
  },
  domain: 'https://wavecraft-audio.com',
  announcement: {
    enabled: true,
    text: 'Emberline introductory pricing — 30 days',
    linkLabel: 'Discover Emberline',
    href: '/plugins/emberline',
  },
  moonbase: {
    accountUrl: 'https://wavecraft-audio.moonbase.sh',
    storefrontScript: 'https://assets.moonbase.sh/storefront/moonbase.js',
    showSetupNotice: false,
  },
  analytics: {
    googleAnalyticsId: '',
  },
  turnstile: {
    siteKey: '',
  },
  social: {
    instagram: '',
    youtube: '',
    soundcloud: '',
  },
  compatibility: {
    headline: 'Built for modern Mac sessions',
    formats: ['Standalone', 'AUv3', 'VST3'],
    details: ['Apple Silicon native', 'Intel compatible', 'macOS 14 or newer'],
    note: 'Universal 64-bit installer for compatible macOS hosts.',
  },
  products: [
    {
      slug: 'emberline',
      moonbaseId: 'wc-embr-mac-v1',
      name: 'Emberline',
      category: 'Expressive signal shaping',
      eyebrow: 'A beautiful place between sound',
      shortDescription:
        'Move from familiar to transformed with one musical gesture—while preserving level, impact and the shape of the source.',
      description:
        'Emberline coordinates tone, density, motion and space behind a single Amount control. Mix, ducking, oversampling and level management keep the result grounded, so experimentation stays fast and comparisons stay honest.',
      priceFallback: '$24.99',
      listPriceFallback: '$39',
      artwork: '/assets/plugins/emberline-v1.png',
      accent: '#f5a20b',
      version: '1.0.0',
      formats: ['Standalone', 'AUv3', 'VST3'],
      systemRequirements: ['macOS 14 or newer', 'Apple Silicon or Intel', '64-bit compatible host'],
      features: [
        {
          title: 'One expressive gesture',
          description:
            'Amount moves a network of musical relationships together, making broad transformations feel coherent instead of layered-on.',
        },
        {
          title: 'Impact stays in the room',
          description:
            'Ducking, mix control and level-aware output help preserve attacks and make processed-to-bypass comparisons meaningful.',
        },
        {
          title: 'Finish-quality control',
          description:
            'Selectable oversampling, calibrated gain staging and clear metering let Emberline move from exploration to a committed mix decision.',
        },
      ],
      idealFor: ['Vocals', 'Guitars', 'Drums', 'Synths', 'Buses', 'Sound design'],
      commerce: {
        trialDays: 10,
        activations: 3,
        launchDuration: '30 days',
        educationPrice: '$19',
        educationListPrice: '$29',
      },
    },
  ] satisfies Product[],
  changelog: [
    {
      date: '2026-08-09',
      product: 'Emberline',
      version: '1.0.0',
      notes: ['Initial macOS release', 'Standalone, AUv3 and VST3 formats', 'Universal Apple Silicon and Intel installer'],
    },
  ],
} as const;

export const products: readonly Product[] = siteConfig.products;

export function getProductBySlug(slug: string): Product | undefined {
  return products.find((product) => product.slug === slug);
}

export function isMoonbaseConfigured(): boolean {
  return siteConfig.moonbase.accountUrl.startsWith('https://');
}
